import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  ShouldRevalidateFunction,
  useMatches,
  useRouteError,
} from "@remix-run/react";
import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import { DehydratedState, HydrationBoundary, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createWSClient,
  loggerLink,
  splitLink,
  unstable_httpBatchStreamLink,
  unstable_httpSubscriptionLink,
  wsLink,
} from "@trpc/client";
import { getTransformer } from "@trpc/client/unstable-internals";
import { posthog } from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { ReactNode, Suspense, useEffect, useMemo, useState } from "react";
import { useHydrated } from "remix-utils/use-hydrated";
import { SuperJSON } from "superjson";
import { z } from "zod";
import { CookieBanner, ErrorPage, ThemeProvider } from "~/components";
import { MainRouter } from "./.server";
import { PaywallProvider } from "./components/PaywallProvider";
import { MessageToStreamTimingProvider } from "./hooks";
import "./root.css";
import { loader } from "./root.server";
import { trpc, useRootLoaderData } from "./utils";
import {
  createWsTransportHealth,
  preferHttpWhenWsBrokenLink,
  subscriptionWithFallbackLink,
  websocketUrl,
  WsTransportHealth,
} from "./utils/trpc-transport";

export { loader };

// never revalidate (should be static data anyways)
export const shouldRevalidate: ShouldRevalidateFunction = () => false;

// Applies the theme before first paint to avoid a flash of the wrong theme.
// Must stay in sync with ThemeProvider (cookie name, colors, class toggling).
const themeInitScript = `(function () {
  try {
    var match = document.cookie.match(/(?:^|;\\s*)moneta_theme=(dark|light|system)(?=\\s*(?:;|$))/);
    var preference = match ? match[1] : "system";
    var dark =
      preference === "dark" ||
      (preference === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
    document.documentElement.classList.toggle("dark", dark);
    var meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", dark ? "#161616" : "#ffffff");
  } catch (_) {
    // never block rendering on theme detection
  }
})();`;

export function Layout({ children }: { children: ReactNode }) {
  return (
    // suppressHydrationWarning: the theme script above adds the "dark" class
    // before React hydrates, which is an expected html-attribute mismatch
    <html lang="en" className="h-full" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
        <link rel="apple-touch-icon" sizes="180x180" href="/__moneta-static__/apple-touch-icon.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/__moneta-static__/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/__moneta-static__/favicon-16x16.png" />
        <link rel="manifest" href="/__moneta-static__/site.webmanifest" />
        <link rel="mask-icon" href="/__moneta-static__/safari-pinned-tab.svg" color="#5bbad5" />
        <meta name="msapplication-TileColor" content="#da532c" />
        {/* suppressHydrationWarning: the theme script rewrites content pre-hydration */}
        <meta name="theme-color" content="#ffffff" suppressHydrationWarning />
        <script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
        <Meta />
        <Links />
      </head>
      <body className="h-full">
        <Suspense fallback="Loading...">{children}</Suspense>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

const titleForStatus: Partial<Record<number, string>> = {
  [400]: "Bad request.",
  [401]: "Unauthorized.",
  [403]: "Forbidden.",
  [404]: "Not found.",
  [405]: "Method not allowed.",
  [406]: "Not acceptable.",

  [500]: "Something went wrong.",
  [501]: "Not implemented.",
  [502]: "Bad gateway.",
  [503]: "Service unavailable.",
  [504]: "Gateway timeout.",
};
const defaultTitle = "Something went wrong.";

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);

  if (isRouteErrorResponse(error)) {
    return <ErrorPage status={error.status} title={titleForStatus[error.status] ?? defaultTitle} />;
  }

  return <ErrorPage status={1000} title={defaultTitle} />;
};

const transformer = getTransformer(SuperJSON);

const getBatchLink = () =>
  unstable_httpBatchStreamLink<MainRouter>({
    url: "/trpc",
    transformer,
    headers: {
      "x-moneta-proxy-target": "remix",
    },
    maxItems: 10,
  });

/** Must match the path in the worker. */
const WS_PATH = "/trpc/ws";

const getWsLink = (health: WsTransportHealth) =>
  wsLink<MainRouter>({
    client: createWSClient({
      // Absolute, resolved at connect time: tRPC passes this straight to
      // `new WebSocket()`, and Safari ≤16 throws on a relative URL there
      // (#1213 — see `websocketUrl`).
      url: () => websocketUrl(WS_PATH, window.location.href),
      lazy: {
        enabled: true,
        closeMs: 60_000, // 1 minute
      },
      ...health.wsClientCallbacks,
    }),
    transformer,
  });

/**
 * The conversation stream over plain HTTP `text/event-stream` (#1230 point 5).
 * Same procedure, same event vocabulary — only the browser↔worker hop changes,
 * since the worker's own WebSocket to the ConversationalObjectDO is internal to
 * Cloudflare and unaffected by whatever the client can or cannot open.
 */
const getSubscriptionFallbackLink = () =>
  unstable_httpSubscriptionLink<MainRouter>({
    url: "/trpc",
    transformer,
  });

/**
 * `app.ws.*` is the WebSocket namespace, but only its subscription genuinely
 * needs a socket — the mutations are ordinary procedures the HTTP link serves
 * fine. Both fall back independently (see `utils/trpc-transport.ts`), so a
 * ws-less page loads its conversation *and* can still act on it.
 */
const isWsNamespace = (path: string) => path.split(".").some((part) => part === "ws");

const getTrpcLinks = (health: WsTransportHealth) => {
  const ws = getWsLink(health);
  const batch = getBatchLink();
  return [
    loggerLink(),
    splitLink({
      condition: (op) => isWsNamespace(op.path),
      true: splitLink({
        condition: (op) => op.type === "subscription",
        true: subscriptionWithFallbackLink<MainRouter>({
          primary: ws,
          fallback: getSubscriptionFallbackLink(),
          health,
        }),
        false: preferHttpWhenWsBrokenLink<MainRouter>({ ws, http: batch, health }),
      }),
      false: batch,
    }),
  ];
};

export const Posthog = ({ children }: { children: ReactNode }) => {
  const { user, posthog: posthogConfig } = useRootLoaderData();
  const isLoggedOut = !user;

  useEffect(() => {
    if (!posthogConfig) {
      return;
    }

    // logged-in users
    if (user) {
      posthog.init(posthogConfig.apiKey, {
        api_host: posthogConfig.host,
        person_profiles: "identified_only",
        debug: posthogConfig.debug,
      });
      posthog.identify(user.id, {
        username: user.username,
      });
    } else {
      // logged-out users
      posthog.init(posthogConfig.apiKey, {
        api_host: posthogConfig.host,
        person_profiles: "identified_only",
        debug: posthogConfig.debug,
        persistence: "memory", // persistence can be enabled in ~/components/CookieBanner.tsx
      });
    }
  }, [posthogConfig, user]);

  return (
    <PostHogProvider client={posthog}>
      {isLoggedOut && <CookieBanner />}
      {children}
    </PostHogProvider>
  );
};

function App() {
  const [queryClient] = useState(() => new QueryClient());

  const matches = useMatches();

  // Initialize tRPC client only on the client-side to avoid SSR issues with
  // WebSocket links. Gated on hydration (not `typeof window`) so the first
  // client render returns null just like the server did — `typeof window` is
  // already defined on the *first* client render, so React hydrates a full app
  // tree against server HTML that holds nothing but an empty Suspense boundary,
  // then throws the whole tree away and regenerates it (#1180, #1217).
  //
  // `useHydrated` is load-bearing, and specifically because of *how* it is
  // implemented: `useSyncExternalStore(subscribe, () => true, () => false)`.
  // React calls `getServerSnapshot()` (false) for as long as it is hydrating
  // and only flips to true from a passive effect, which runs after the
  // hydration commit — so the divergence cannot happen mid-hydration. Any gate
  // that can read true *during* the first render reintroduces the mismatch.
  //
  // #1373 is the confirming incident: production served a build predating
  // #1217 and reported React #418 on every dashboard page load, in every
  // engine, for 25 days. `e2e/tests/hydration.spec.ts` guards this now, and
  // `scripts/preview/hydration-probe.mjs` names the failing element when it
  // does break (production React reports #418 with no arguments).
  const isHydrated = useHydrated();
  const trpcClient = useMemo(() => {
    if (!isHydrated) {
      return null;
    }

    // WebSocket stays primary for the `ws` namespace; a page that cannot open
    // one degrades to SSE (subscriptions) and HTTP (mutations) instead of
    // hanging on "Loading conversation..." forever — #1230 point 5.
    const health = createWsTransportHealth({
      onFallBack: (reason) => {
        console.warn(`[trpc] WebSocket unusable (${reason}) — falling back to SSE/HTTP for the ws namespace`);
      },
    });

    return trpc.createClient({ links: getTrpcLinks(health) });
  }, [isHydrated]);

  // Show loading state while client is being initialized
  if (!trpcClient) {
    return null;
  }

  const content = matches
    .map((m) => {
      try {
        return z.object({ trpcState: z.unknown() }).parse(m.data).trpcState;
      } catch {
        return undefined;
      }
    })
    .filter((state) => Boolean(state))
    .map((state) => transformer.input.deserialize(state) as DehydratedState)
    .reduceRight(
      (children, state) => <HydrationBoundary state={state}>{children}</HydrationBoundary>,
      <MessageToStreamTimingProvider>
        <Outlet />
      </MessageToStreamTimingProvider>,
    );

  return (
    <ThemeProvider>
      <Posthog>
        <trpc.Provider client={trpcClient} queryClient={queryClient}>
          <PaywallProvider>
            <QueryClientProvider client={queryClient}>{content}</QueryClientProvider>
          </PaywallProvider>
        </trpc.Provider>
      </Posthog>
    </ThemeProvider>
  );
}

export default withSentry(App);
